import { useRef, useState } from 'react'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, Table, TableHead, TableRow, TableCell, TableBody,
  Alert, CircularProgress, Chip, Typography, TextField, MenuItem, Divider,
} from '@mui/material'
import { Description, LocalShipping, ReceiptLong, UploadFile, Download } from '@mui/icons-material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  getShipment, shipShipment, transitShipment, deliverShipment, recordShipmentException,
  getShipmentAvizHtml, getShipmentDocumentTransportHtml, getShipmentBonHtml, uploadShipmentPod, getShipmentPod,
  type DeliveryExceptionType,
} from '../../api/logistica'
import { errorMessage } from '../../api/client'

const STATUS_LABEL = { Pregatita: 'Pregătită', Expediata: 'Expediată', InTranzit: 'În tranzit', Livrata: 'Livrată' } as const
const EXCEPTION_LABEL: Record<DeliveryExceptionType, string> = {
  Intarziere: 'Întârziere', ComandaModificata: 'Comandă modificată', AdresaGresita: 'Adresă greșită',
  RefuzLivrare: 'Refuz la livrare', PagubaTransport: 'Pagubă de transport', LivrarePartiala: 'Livrare parțială',
}

export default function ShipmentDetailDialog({ id, onClose }: { id: string | null; onClose: () => void }) {
  const open = id !== null
  const qc = useQueryClient()
  const { data } = useQuery({ queryKey: ['logistica', 'expeditii', id], queryFn: () => getShipment(id!), enabled: open })
  const [podNotes, setPodNotes] = useState('')
  const [quantityOverrides, setQuantityOverrides] = useState<Record<string, string>>({})
  const [exceptionType, setExceptionType] = useState<DeliveryExceptionType | ''>('')
  const [exceptionNotes, setExceptionNotes] = useState('')
  const [docError, setDocError] = useState<string | null>(null)

  const invalidate = () => { void qc.invalidateQueries({ queryKey: ['logistica', 'expeditii'] }) }

  const shipMutation = useMutation({ mutationFn: () => shipShipment(id!), onSuccess: invalidate })
  const transitMutation = useMutation({ mutationFn: () => transitShipment(id!), onSuccess: invalidate })
  const deliverMutation = useMutation({
    mutationFn: () => deliverShipment(id!, {
      podNotes: podNotes || null,
      lines: data!.lines.map((l) => ({ pickingListLineId: l.id, deliveredQuantity: Number(quantityOverrides[l.id] ?? l.quantity) })),
    }),
    onSuccess: invalidate,
  })
  const exceptionMutation = useMutation({
    mutationFn: () => recordShipmentException(id!, exceptionType as DeliveryExceptionType, exceptionNotes || null),
    onSuccess: () => { invalidate(); setExceptionType(''); setExceptionNotes('') },
  })

  const podFileInputRef = useRef<HTMLInputElement>(null)
  const podUploadMutation = useMutation({
    mutationFn: (file: File) => uploadShipmentPod(id!, file),
    onSuccess: invalidate,
    onError: (e) => setDocError(errorMessage(e)),
  })
  const downloadPod = async () => {
    try {
      const blob = await getShipmentPod(id!)
      window.open(URL.createObjectURL(blob), '_blank')
    } catch (e) {
      setDocError(errorMessage(e))
    }
  }

  const openDocument = async (loader: (id: string) => Promise<string>) => {
    try {
      const html = await loader(id!)
      const win = window.open('', '_blank')
      if (win) { win.document.write(html); win.document.close() }
    } catch (e) {
      setDocError(errorMessage(e))
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Expediere {data?.number} — {data?.orderNumber} ({data?.clientName})
        {data && <Chip size="small" sx={{ ml: 1.5 }} label={STATUS_LABEL[data.status]} color={data.status === 'Livrata' ? 'success' : 'info'} />}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5}>
          {data && (
            <Typography variant="body2" color="text.secondary">
              Transportator: {data.carrierName} {data.vehiclePlate ? `(${data.vehiclePlate})` : ''} — {data.isOwnFleet ? 'flotă proprie' : 'transportator terț'}
              {data.transportCost != null && ` · Cost transport: ${data.transportCost.toFixed(2).replace('.', ',')}`}
            </Typography>
          )}

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Articol</TableCell><TableCell>Lot</TableCell><TableCell align="right">Alocat</TableCell>
                {data?.status === 'InTranzit' && <TableCell align="right">De livrat</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.lines.map((l) => (
                <TableRow key={l.id}>
                  <TableCell>{l.articleCode} — {l.articleDescription}</TableCell>
                  <TableCell>{l.lotNumber ?? '—'}</TableCell>
                  <TableCell align="right">{l.quantity}</TableCell>
                  {data.status === 'InTranzit' && (
                    <TableCell align="right">
                      <TextField type="number" size="small" value={quantityOverrides[l.id] ?? String(l.quantity)}
                        onChange={(e) => setQuantityOverrides((q) => ({ ...q, [l.id]: e.target.value }))} sx={{ width: 100 }} />
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Stack direction="row" spacing={1}>
            <Button size="small" startIcon={<Description />} onClick={() => void openDocument(getShipmentAvizHtml)}>Aviz</Button>
            <Button size="small" startIcon={<LocalShipping />} onClick={() => void openDocument(getShipmentDocumentTransportHtml)}>
              {data?.isInternational ? 'CMR' : 'Scrisoare transport'}
            </Button>
            <Button size="small" startIcon={<ReceiptLong />} onClick={() => void openDocument(getShipmentBonHtml)}>Bon predare-primire</Button>
          </Stack>
          {docError && <Alert severity="error" onClose={() => setDocError(null)}>{docError}</Alert>}

          {data?.status === 'InTranzit' && (
            <>
              <Divider />
              <Typography variant="subtitle2">Livrează</Typography>
              <TextField label="Notă dovadă de livrare (opțional)" value={podNotes} onChange={(e) => setPodNotes(e.target.value)} fullWidth multiline minRows={2} />
              {deliverMutation.isError && <Alert severity="error">{errorMessage(deliverMutation.error)}</Alert>}
            </>
          )}

          {data?.status === 'Livrata' && (
            <>
              <Alert severity="success">
                Livrată la {data.deliveredAt && new Date(data.deliveredAt).toLocaleString('ro-RO')}
                {data.podNotes && ` — ${data.podNotes}`}
              </Alert>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Button size="small" startIcon={<UploadFile />} disabled={podUploadMutation.isPending}
                  onClick={() => podFileInputRef.current?.click()}>
                  Atașează dovadă de livrare (poză/semnătură)
                </Button>
                {data.podPhotoFileName && (
                  <Button size="small" startIcon={<Download />} onClick={() => void downloadPod()}>Descarcă</Button>
                )}
              </Stack>
              <input ref={podFileInputRef} type="file" hidden accept="image/*" onChange={(e) => {
                const file = e.target.files?.[0]
                if (file) podUploadMutation.mutate(file)
                e.target.value = ''
              }} />
            </>
          )}

          {data && data.status !== 'Livrata' && (
            <>
              <Divider />
              <Typography variant="subtitle2">Înregistrează excepție (opțional)</Typography>
              <Stack direction="row" spacing={1.5}>
                <TextField select size="small" label="Tip" value={exceptionType} onChange={(e) => setExceptionType(e.target.value as DeliveryExceptionType)} sx={{ width: 220 }}>
                  {Object.entries(EXCEPTION_LABEL).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
                </TextField>
                <TextField size="small" label="Notă" value={exceptionNotes} onChange={(e) => setExceptionNotes(e.target.value)} sx={{ flexGrow: 1 }} />
                <Button size="small" variant="outlined" color="warning" disabled={!exceptionType || exceptionMutation.isPending} onClick={() => exceptionMutation.mutate()}>
                  Înregistrează
                </Button>
              </Stack>
              {data.exceptionType && (
                <Alert severity="warning">{EXCEPTION_LABEL[data.exceptionType]}{data.exceptionNotes && ` — ${data.exceptionNotes}`}</Alert>
              )}
              {exceptionMutation.isError && <Alert severity="error">{errorMessage(exceptionMutation.error)}</Alert>}
            </>
          )}

          {(shipMutation.isError || transitMutation.isError) && (
            <Alert severity="error">{errorMessage(shipMutation.error ?? transitMutation.error)}</Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose}>Închide</Button>
        {data?.status === 'Pregatita' && (
          <Button variant="contained" disabled={shipMutation.isPending} onClick={() => shipMutation.mutate()}
            startIcon={shipMutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}>
            Expediază
          </Button>
        )}
        {data?.status === 'Expediata' && (
          <Button variant="contained" disabled={transitMutation.isPending} onClick={() => transitMutation.mutate()}
            startIcon={transitMutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}>
            În tranzit
          </Button>
        )}
        {data?.status === 'InTranzit' && (
          <Button variant="contained" disabled={deliverMutation.isPending} onClick={() => deliverMutation.mutate()}
            startIcon={deliverMutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}>
            Confirmă livrarea
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
